/* eslint-disable react/jsx-one-expression-per-line */
import React, { useRef, useState, useEffect } from 'react';
// @ts-ignore
import { Loader } from '@els/els-react--loader';
// @ts-ignore
import { Toast } from '@els/els-react--toast';
// @ts-ignore
import { Icon } from '@els/els-react--icon';
import parse from 'html-react-parser';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
    selectHSCSContentAsyncStatus,
    selectHscsContentDetails,
    selectHscsStatusCode,
} from '../search-selector';
import './patient-education-content.scss';
import { getParsedContent, formatDate } from '../../../utils/helper';
import {
    hasFailed,
    hasSucceeded,
    isPending,
} from '../../../../libs/utils/constants/async-status';
import { getPEContentFromHSCS } from '../search-thunk';
import PrintTab from '../../../utils/print-tab/print-tab';
import { BannerLinks } from '../../../utils/banner-links.tsx/banner-links';
import {
    languageCode,
    LanguageCodeType,
} from '../../../constants/language-codes';
import ServerErrorPage from '../../../components/server-error-page/server-error-page';
import BackButton from '../../../components/back-button/back-button';
import { IRootState } from '../../../app/store';
import EmailModal from '../../../components/email-modal/email-modal';
import { selectAppConfigs } from '../../app-config/app-config-selector';
import BackButtonNavigationTracking from '../../../utils/back-button-helper';
import { cancelledAsyncLogin } from '../../login/login-slice';
import { selectUserAuthenticationStatus } from '../../login/login-selector';
import { selectSendSkillsEmailAsyncStatus } from '../../../components/email-modal/email-selector';
import AriaLiveAnnouncer from '../../../components/aria-live-announcer/aria-live-announcer';
import { resetSendPatientEducationEmailAsyncStatus } from '../../../components/email-modal/email-slice';

const PatientEducationContent = () => {
    const location = useLocation();
    const { ecrId } = useParams<{ ecrId: string }>();
    const queryParams = new URLSearchParams(location.hash.replace(/^#/, ''));
    const lang = queryParams.get('lang');
    const id = queryParams.get('id');
    const esid = queryParams.get('esid') || '';
    const languageToIdMap = {
        eng: sessionStorage.getItem('peQueryParamsEid') || '',
        es: sessionStorage.getItem('peQueryParamsSpanishId') || '',
    };
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageCodeType>(
        (lang as LanguageCodeType) ||
            (sessionStorage.getItem(
                'peQueryParamsLanguageCode'
            ) as LanguageCodeType) ||
            languageCode.ENGLISH
    );
    const [selectedId, setSelectedId] = useState(
        languageToIdMap[selectedLanguage]
    );
    const baseClassName = 'patient-education-content';
    const contentLang = selectedLanguage === languageCode.ENGLISH ? 'en' : 'es';
    const hscsContentDetails = useSelector(selectHscsContentDetails);
    const hscsAsyncStatus = useSelector(selectHSCSContentAsyncStatus);
    const appConfigs = useSelector(selectAppConfigs);
    const [PeTitle, setPeTitle] = React.useState<React.ReactNode>('');
    const [PeContent, setPeContent] = React.useState<React.ReactNode>(null);
    const [copyRight, setCopyRight] = React.useState<React.ReactNode>(null);
    const [revisedDate, setRevisedDate] = React.useState<string | null>(null);
    const [reviewedDate, setReviewedDate] = React.useState<string | null>(null);
    const [isPrintTabVisible, setIsPrintTabVisible] = useState(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [isCopySuccess, setIsCopySuccess] = useState(false);
    const componentRef = useRef(null);
    const copySuccessTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const copyLinkButtonRef = useRef<HTMLButtonElement>(null);
    const dispatch = useDispatch();
    const isUserAuthenticated = useSelector(selectUserAuthenticationStatus);
    const sendPatientEducationEmailStatus = useSelector(
        selectSendSkillsEmailAsyncStatus
    );
    const isEmailSentSuccess = hasSucceeded(sendPatientEducationEmailStatus);
    const [showEmailSentToast, setShowEmailSentToast] = useState(false);
    const [emailToastKey, setEmailToastKey] = useState(0);
    const emailSentToastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!(localStorage.getItem('IS_USER_AUTHENTICATED') === 'true')) {
            dispatch(cancelledAsyncLogin());
        }
        if (lang && id) {
            setSelectedId(lang === languageCode.ENGLISH ? id : esid);
            languageToIdMap[languageCode.ENGLISH] = id;
            languageToIdMap[languageCode.SPANISH] = esid;
            sessionStorage.setItem('peQueryParamsEid', id);
            sessionStorage.setItem('peQueryParamsSpanishId', esid);
        }
    }, [id, lang, esid]);

    useEffect(() => {
        if (selectedId !== '') {
            dispatch(getPEContentFromHSCS(selectedId));
        }
    }, [selectedId]);

    const parseContent = (content: string) => {
        const parsedTitle = getParsedContent('body .title', content);
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(
            hscsContentDetails,
            'application/xml'
        );

        let mappedBodyContent = null;
        if (xmlDoc) {
            mappedBodyContent = Array.from(
                xmlDoc.querySelectorAll('body .body')
            ).map((item) => item.innerHTML);
        }

        const parsedMappedBodyContent = mappedBodyContent
            ? parse(mappedBodyContent.join(''))
            : null;

        const parsedCopyRight = getParsedContent(
            'body .copyright_statement',
            content
        );

        // Handle date_updated
        const dateMonth =
            xmlDoc.querySelector('body .date_updated .date_month')
                ?.textContent || '';
        const dateYear =
            xmlDoc.querySelector('body .date_updated .date_year')
                ?.textContent || '';
        let parsedRevisedDate: string | null = null;
        if (dateMonth && dateYear) {
            const date = new Date(`${dateMonth} 1, ${dateYear}`);
            if (!Number.isNaN(date.getTime())) {
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                parsedRevisedDate = `${month}/${year}`;
            }
        } else {
            parsedRevisedDate = xmlDoc
                .querySelector('body .date_updated')
                ?.textContent?.trim()
                ? formatDate(
                      xmlDoc
                          .querySelector('body .date_updated')
                          ?.textContent?.trim()
                  )
                : null;
        }

        // Handle date_reviewed
        const parsedReviewedDate = xmlDoc
            .querySelector('body .date_reviewed')
            ?.textContent?.trim()
            ? formatDate(
                  xmlDoc
                      .querySelector('body .date_reviewed')
                      ?.textContent?.trim()
              )
            : null;

        return {
            parsedTitle,
            parsedCopyRight,
            parsedRevisedDate,
            parsedReviewedDate,
            parsedMappedBodyContent,
        };
    };

    useEffect(() => {
        if (hscsContentDetails) {
            const {
                parsedTitle,
                parsedCopyRight,
                parsedReviewedDate,
                parsedRevisedDate,
                parsedMappedBodyContent,
            } = parseContent(hscsContentDetails);
            setPeTitle(parsedTitle);
            setPeContent(parsedMappedBodyContent);
            setCopyRight(parsedCopyRight);
            setRevisedDate(parsedRevisedDate);
            setReviewedDate(parsedReviewedDate);
        }
    }, [hscsContentDetails]);

    useEffect(() => {
        const descriptionDiv = document.querySelector(
            '.patient-education-content__description'
        );
        if (descriptionDiv) {
            const languageTitle = descriptionDiv.querySelector(':scope > h2');
            if (languageTitle) {
                try {
                    languageTitle.remove();
                } catch (error) {
                    console.error('Error removing h2:', error);
                }
            }
        }
    }, [PeContent]);

    const hscsStatusCode = useSelector(selectHscsStatusCode);

    const errorType = hasFailed(hscsAsyncStatus) && hscsStatusCode !== 200;

    const handlePrintClick = (data: boolean) => {
        setIsPrintTabVisible(data);
    };

    const onSelectLanguage = (language: LanguageCodeType) => {
        if (language !== selectedLanguage) {
            setSelectedLanguage(language);
            sessionStorage.setItem('peQueryParamsLanguageCode', language);
            setSelectedId(languageToIdMap[language]);

            if (lang && id) {
                const newURL = `lang=${language}&id=${
                    languageToIdMap[languageCode.ENGLISH]
                }&esid=${languageToIdMap[languageCode.SPANISH]}`;
                window.history.replaceState(
                    null,
                    '',
                    `${window.location.pathname}#${newURL}`
                );
            }
        }
    };
    const historyStack = useSelector(
        (state: IRootState) => state.navigation.history
    );
    const canGoBack = historyStack.length > 1;
    const handleEmail = (data: boolean) => {
        setIsEmailModalOpen(data);
    };

    const handleCopyLink = () => {
        const peLink = `${appConfigs.POINT_OF_CARE_BASE_URL}/
            ${ecrId}/patient-education#
            lang=${selectedLanguage}&
            id=${languageToIdMap[languageCode.ENGLISH]}&
            esid=${languageToIdMap[languageCode.SPANISH]}`;
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard
                .writeText(peLink.replace(/\s/g, ''))
                .then(() => {
                    setIsCopySuccess(true);
                })
                .catch((err) => {
                    console.error('Failed to copy: ', err);
                    fallbackCopyTextToClipboard(peLink.replace(/\s/g, ''));
                });
        } else {
            fallbackCopyTextToClipboard(peLink);
        }
    };

    const fallbackCopyTextToClipboard = (text: string) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            const successful = document.execCommand('copy');
            if (successful) {
                setIsCopySuccess(true);
            } else {
                console.error(
                    'Fallback: Copying text command was unsuccessful'
                );
            }
        } catch (err) {
            console.error('Fallback: Oops, unable to copy', err);
        }
        document.body.removeChild(textArea);
    };

    const handleCopySuccessClose = () => {
        if (copySuccessTimeoutRef.current) {
            clearTimeout(copySuccessTimeoutRef.current);
            copySuccessTimeoutRef.current = null;
        }
        setIsCopySuccess(false);
        copyLinkButtonRef.current?.focus();
    };

    useEffect(() => {
        if (isCopySuccess) {
            copySuccessTimeoutRef.current = setTimeout(() => {
                // Focus would otherwise be dropped to <body> if the user tabbed
                // into the toast (e.g. its close button) and never clicked it
                if (
                    document.activeElement?.closest(
                        `.${baseClassName}__copy-link-toast`
                    )
                ) {
                    copyLinkButtonRef.current?.focus();
                }
                setIsCopySuccess(false);
                copySuccessTimeoutRef.current = null;
            }, 5000);
        }

        return () => {
            if (copySuccessTimeoutRef.current) {
                clearTimeout(copySuccessTimeoutRef.current);
                copySuccessTimeoutRef.current = null;
            }
        };
    }, [isCopySuccess]);

    useEffect(() => {
        if (isEmailSentSuccess) {
            if (emailSentToastTimeoutRef.current) {
                clearTimeout(emailSentToastTimeoutRef.current);
                emailSentToastTimeoutRef.current = null;
            }
            setEmailToastKey((prev) => prev + 1);
            setShowEmailSentToast(true);
            dispatch(resetSendPatientEducationEmailAsyncStatus());
            emailSentToastTimeoutRef.current = setTimeout(() => {
                setShowEmailSentToast(false);
                emailSentToastTimeoutRef.current = null;
            }, 5000);
        }

        return () => {
            if (emailSentToastTimeoutRef.current) {
                clearTimeout(emailSentToastTimeoutRef.current);
                emailSentToastTimeoutRef.current = null;
            }
        };
    }, [dispatch, isEmailSentSuccess]);

    return (
        <>
            {canGoBack && window.history.length > 0 && <BackButton />}
            {errorType && <ServerErrorPage />}
            {isPending(hscsAsyncStatus) && (
                <Loader
                    isVisible
                    isBlocking={false}
                    className={`${baseClassName}-loader`}
                />
            )}
            <AriaLiveAnnouncer
                message={isCopySuccess ? 'Link copied to clipboard.' : ''}
            />
            {hasSucceeded(hscsAsyncStatus) && (
                <div ref={componentRef}>
                    {showEmailSentToast && (
                        <Toast
                            key={emailToastKey}
                            autoHide
                            color="confirm"
                            contained
                            className={`${baseClassName}__email-toast`}
                        >
                            <Icon
                                sprite="ConfirmationOutlineCircle"
                                color="positive"
                                isVisible
                                a11y={{
                                    name: 'Confirmation Circle',
                                    description: 'Confirmation Circle',
                                }}
                            />
                            &nbsp;Email sent successfully.
                        </Toast>
                    )}
                    <div
                        className={`${baseClassName}__header`}
                        lang={contentLang}
                    >
                        {PeTitle}
                    </div>
                    <div className={`${baseClassName}__links`} lang="en">
                        <BannerLinks
                            handlePrintClick={handlePrintClick}
                            onSelectLanguage={onSelectLanguage}
                            currentLanguage={selectedLanguage}
                            handleEmail={handleEmail}
                            handleCopyLink={handleCopyLink}
                            isUserAuthenticated={isUserAuthenticated}
                            copyLinkButtonRef={copyLinkButtonRef}
                            copyLinkToast={
                                isCopySuccess && (
                                    <Toast
                                        autoHide={false}
                                        color="confirm"
                                        contained
                                        onClose={handleCopySuccessClose}
                                        className={`${baseClassName}__copy-link-toast`}
                                    >
                                        <Icon
                                            sprite="ConfirmationOutlineCircle"
                                            color="positive"
                                            isVisible
                                            a11y={{
                                                name: 'Confirmation Circle',
                                                description:
                                                    'Confirmation Circle',
                                            }}
                                        />
                                        &nbsp;Link copied to clipboard.
                                    </Toast>
                                )
                            }
                        />
                    </div>
                    <div
                        className={`${baseClassName}__description`}
                        lang={contentLang}
                    >
                        {PeContent}
                    </div>
                    <div className={`${baseClassName}__footer`} lang="en">
                        <div className={`${baseClassName}__footer__date`}>
                            {revisedDate
                                ? `Document Revised: ${revisedDate}`
                                : ''}
                            &nbsp;{' '}
                            {reviewedDate
                                ? `Document Reviewed: ${reviewedDate}`
                                : ''}
                        </div>
                        <div>{copyRight}</div>
                    </div>
                    {isPrintTabVisible && (
                        <PrintTab
                            printTabRef={componentRef}
                            callback={handlePrintClick}
                        />
                    )}
                    {isEmailModalOpen && (
                        <EmailModal
                            onCloseHandler={() => setIsEmailModalOpen(false)}
                            isEmailModalOpen={isEmailModalOpen}
                            contentTitle={PeTitle}
                            ecrId={ecrId}
                            lang={selectedLanguage}
                            id={languageToIdMap[languageCode.ENGLISH]}
                            esid={languageToIdMap[languageCode.SPANISH]}
                        />
                    )}
                </div>
            )}
        </>
    );
};
export default BackButtonNavigationTracking(
    'Patient Education',
    PatientEducationContent
);
